/// <reference types="vite-plugin-svgr/client" />
import { ScopedCssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { FormFieldDefaults } from "@zfp-akademie-a-s/zfp-design/form";
import { Toaster } from "@zfp-akademie-a-s/zfp-design/toaster";
import { Configuration } from "Infrastructure/Config/config";
import { UserContextProvider } from "Infrastructure/Context/UserContext";
import { ApiCallError, handleResponseError } from "Infrastructure/Errors/ApiCallError";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { ApplicationErrorPage } from "Infrastructure/Pages/ApplicationErrorPage";
import { getTheme } from "Infrastructure/Themes/Themes";
import { CzechResourcesDictionary } from "Infrastructure/Translations/CzechResourcesDictionary";
import { initSentry } from "Infrastructure/Utils/SentryUtils";
import i18n from "i18next";
import { ErrorBoundary } from "react-error-boundary";
import { initReactI18next } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import { z } from "zod";
import { zodI18nMap } from "zod-i18n-map";
import { appRouter } from "./Infrastructure/Routing/AppRouter";

function App() {
  initSentry();
  Configuration.initConfig();

  const theme = getTheme();
  const dateLocale = useDateFnsLocale();
  i18n.use(initReactI18next).init({
    resources: {
      cs: CzechResourcesDictionary,
    },
    lng: "cs",
    fallbackLng: "cs",
    interpolation: {
      escapeValue: false,
    },
  });

  z.setErrorMap(zodI18nMap);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
      mutations: {
        onError: handleResponseError,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.suppressErrorToast) return;
        if (ApiCallError.isApiException(error)) {
          handleResponseError(error);
        }
      },
    }),
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      <ThemeProvider theme={theme}>
        <Toaster />
        <ScopedCssBaseline>
          <ErrorBoundary FallbackComponent={ApplicationErrorPage}>
            <UserContextProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                <FormFieldDefaults reserveHelperSpace={false}>
                  <RouterProvider router={appRouter} />
                </FormFieldDefaults>
              </LocalizationProvider>
            </UserContextProvider>
          </ErrorBoundary>
        </ScopedCssBaseline>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
export default App;
